import {GraphQL} from "../graphql"
import * as React from "react"
import {ExtraChargeList, ExtraChargeListState} from "./ExtraChargeList"
import {Form, Loader, Message} from "semantic-ui-react"
import {Charges, ChargesOuterState} from "./Charges"
import {SaveResetButtons, SectionProps, UpdatableFormSection} from "./UpdateBookingWizard"
import {LoyaltyProgramDiscount, SetBookingPrice} from "../graphql/queries"
import {errorUnionToEither} from "../graphql/errors"
import {extraChargesToInputExtraCharges} from "./BookingWizard"
import {Charge} from "../model/BookingTypes"
import {bookingDateTime, bookingToBookingDetails} from "./BookingDetails"

interface BookingPricingState {
  extraCharges: ExtraChargeListState
  price: ChargesOuterState
}

interface BookingPricingSectionProps extends SectionProps {}

export function BookingPricingSection(props: BookingPricingSectionProps) {
  const {
    booker: {isAdmin},
    booking,
  } = props
  const state: BookingPricingState = {
    extraCharges: {
      isEditing: false,
      index: null,
      extraCharges: props.booking.extraCharges,
    },
    price: {
      editingPrice: false,
      price: props.booking.price,
    },
  }
  const bookingDetails = bookingToBookingDetails(booking)
  return (
    <UpdatableFormSection
      {...props}
      header="Pricing"
      state={state}
      render={(params) => {
        const {state, mapState} = params
        const anyEditing = state.extraCharges.isEditing || state.price.editingPrice
        return (
          <Form>
            <GraphQL.Query
              query={LoyaltyProgramDiscount}
              // Since this depends on the status of other bookings we always need to fetch it.
              fetchPolicy="cache-and-network"
              variables={{
                bookingDateTime: bookingDateTime(booking),
                journeyId: booking.journey.id,
                forBookerId: booking.booker ? booking.booker.id : null,
                bookingId: booking.id,
                promoCode: booking.promoCode || "",
              }}
              render={({data}) => {
                if (data) {
                  const result = errorUnionToEither(data.loyaltyProgramDiscount)
                  const initial = result.fold(() => false, (data) => data.initial)
                  const warning = result.fold((e) => e.message, () => "")
                  return (
                    <>
                      <Charges
                        {...props}
                        bookingId={props.booking.id}
                        booking={bookingDetails}
                        journeyId={booking.journey.id}
                        initialLoyaltyProgramDiscount={initial}
                        giftLoyaltyProgramDiscount={booking.giftLoyaltyCredits}
                        forBooker={props.booking.booker}
                        stripeCard={props.booking.stripeCard}
                        state={state.price}
                        mapState={
                          mapState &&
                          ((fn) => {
                            mapState((state) => {
                              return {...state, price: fn(state.price)}
                            })
                          })
                        }
                        isAdmin={isAdmin}
                        isInvoiced={props.booking.invoice !== null}
                        extraCharges={[
                          ...booking.automaticExtraCharges,
                          ...state.extraCharges.extraCharges,
                        ]}
                        onApprovePrice={
                          mapState
                            ? null
                            : (price: Charge) => {
                                props.modalStore.handlePromiseThrow(
                                  props.modalStore.applyModal({
                                    pending: ["Approving price", "Approving price..."],
                                    error: (m) => ["Approving price failed", m],
                                    action: async () => {
                                      const data = await GraphQL.mutate(SetBookingPrice, {
                                        id: props.booking.id,
                                        price: {
                                          // We pass up a new object because the "price" object will have a
                                          // "__typename" property at runtime and the GraphQL server barks
                                          // if there are extra properties.
                                          value: {
                                            amount: price.amount,
                                            description: price.description,
                                          },
                                        },
                                      })
                                      return errorUnionToEither(data.setBookingPrice)
                                    },
                                  }),
                                )
                              }
                        }
                      />
                      {warning && (
                        <Message style={{display: "block"}} warning>
                          Warning: {warning}
                        </Message>
                      )}
                    </>
                  )
                }
                return <Loader inline active />
              }}
            />
            <ExtraChargeList
              {...props}
              isAdmin={isAdmin}
              state={state.extraCharges}
              automaticExtraCharges={booking.automaticExtraCharges}
              mapState={
                mapState &&
                ((fn) => {
                  mapState((state) => {
                    return {...state, extraCharges: fn(state.extraCharges)}
                  })
                })
              }
            />
            {mapState && (
              <Form.Group>
                <SaveResetButtons
                  {...props}
                  onSave={
                    anyEditing
                      ? null
                      : async () => {
                          const ret = await props.modalStore.applyModal({
                            pending: ["Setting booking price", "Setting booking price.."],
                            error: (m) => ["Failed to set price", m],
                            action: async () => {
                              const {price} = state.price
                              const data = await GraphQL.mutate(SetBookingPrice, {
                                id: props.booking.id,
                                price: {
                                  // We pass up a new object because the "price" object will have a
                                  // "__typename" property at runtime and the GraphQL server barks
                                  // if there are extra properties.
                                  value: price
                                    ? {
                                        amount: price.amount,
                                        description: price.description,
                                      }
                                    : null,
                                },
                                extraCharges: extraChargesToInputExtraCharges(
                                  state.extraCharges.extraCharges,
                                ),
                              })
                              return errorUnionToEither(data.setBookingPrice)
                            },
                          })
                          return ret.isRight()
                        }
                  }
                />
              </Form.Group>
            )}
          </Form>
        )
      }}
    />
  )
}



// WEBPACK FOOTER //
// ./src/components/PricingSection.tsx